import { skipToken } from '@reduxjs/toolkit/query';
import AuditInfo from 'components/audit/AuditInfo';
import PermissionButton from 'components/buttons/PermissionButton';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { AddressPencilButton } from 'components/forms/inputs/rhf-inputs/AddressInput';
import GridItemList, { infoLabel, Row, spansRow } from 'components/GridItemList';
import Loader from 'components/Loader';
import PageHeader from 'components/PageHeader';
import {
    ActiveChip,
    META_LINK_CLASS,
    MetaItem,
    RECORD_GRID_CLASS,
    RECORD_PANEL_CLASS,
    RECORD_TAB_STRIP_CLASS,
    RECORD_TABS_CLASS,
    RecordHeader,
    RecordPage,
} from 'components/record-header/RecordHeader';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import TabsGallery, { TabType } from 'components/TabsGallery';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import CustomFieldView from 'pages/matters/views/CustomFieldView';
import { useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useGetOfficeByIdQuery } from 'store/api/offices';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { Office } from 'types/offices';
import { getAddress } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useGoBack, useLeaveDeletedEntity, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import CustomFieldCards from './CustomFieldCards';
import { OfficeSection } from './form-sections/OfficeSection';
import { useFixedDeadlineEventSender } from './useFixedDeadlineEventSender';

const USED_CLASSIFIERS = ['COUNTRY'];
const UPDATE_OFFICES = ['UPDATE.OFFICES'];

const mailLink = (address: string | null | undefined) =>
    address ? (
        <a className="entity-link hover:underline" href={`mailto:${address}`}>
            {address}
        </a>
    ) : (
        '-'
    );

/**
 * The office's read rows by name: the classic Overview lists them in one card, the new design by topic. There a
 * row is drawn as the office form's field for it: `chips` where the field is a multi-select, and a list's two
 * defaults only once the list has a template, as the form shows them.
 */
const officeRows = (
    office: Office,
    classifiers: Record<string, ClassifierData>,
    deadlineEventSender: { fixedSender: string | undefined; isKnown: boolean }
) => {
    const countryLabel = office.address
        ? getClassifierText(classifiers['COUNTRY'], office.address.countryCode)
        : undefined;
    const localCountryLabel = office.localAddress
        ? getClassifierText(classifiers['COUNTRY'], office.localAddress.countryCode)
        : undefined;
    const invoiceTemplates = (office.documentTemplates ?? []).filter(
        (template) => template.documentTemplateType === 'INVOICE'
    );
    const invoiceSpecTemplates = (office.documentTemplates ?? []).filter(
        (template) => template.documentTemplateType === 'INVOICE_SPEC'
    );
    const joinTemplateNames = (templates: { id: number; name: string }[]) =>
        templates.length > 0 ? templates.map((template) => template.name).join(', ') : '-';
    const templateName = (template: { name: string } | undefined) => template?.name || '-';
    const chips = (items: { id: number; name: string }[]) => items.map(({ id, name }) => ({ key: id, label: name }));
    // A default's label with the form field's tooltip at the end of its line, so that a label too long for a narrow
    // card wraps where the field's does.
    const defaultLabel = (id: string, tooltipId: string) => infoLabel(id, tooltipId, 'justify-between');
    return {
        company: { label: t('offices.main-company'), content: office.company?.name || '-' },
        companies: {
            label: t('offices.related-companies'),
            content: office.companies?.length ? office.companies.map((company) => company.name).join(', ') : '-',
            chips: chips(office.companies ?? []),
        },
        name: { label: t('name'), content: office.name || '-' },
        initials: { label: t('initials'), content: office.abbreviation || '-' },
        email: { label: t('email'), content: mailLink(office.emailAddress) },
        phone: { label: t('phone-number'), content: office.phoneNumber || '-' },
        address: { label: t('address'), content: office.address ? getAddress(office.address, countryLabel) : '-' },
        invoiceAbbreviation: { label: t('offices.invoice-abbreviation'), content: office.invoiceAbbreviation || '-' },
        localAddress: {
            label: t('address-local'),
            content: office.localAddress ? getAddress(office.localAddress, localCountryLabel) : '-',
        },
        active: { label: t('offices.is-active'), content: office.isActive === false ? t('no') : t('yes') },
        invoicingEmail: { label: t('offices.invoicing-email'), content: mailLink(office.invoicingEmailAddress) },
        deadlineEventEmail: {
            label: t('offices.deadline-event-email'),
            content: deadlineEventSender.isKnown
                ? mailLink(deadlineEventSender.fixedSender ?? office.deadlineEventEmailAddress)
                : '-',
        },
        invoiceTemplates: {
            label: t('offices.invoice-templates'),
            content: joinTemplateNames(invoiceTemplates),
            chips: chips(invoiceTemplates),
        },
        defaultInvoiceTemplate: {
            label: defaultLabel(
                'offices.invoice-templates-default-local',
                'offices.tooltips.invoice-templates-default-local'
            ),
            content: templateName(invoiceTemplates.find((template) => template.isDefault)),
            hidden: invoiceTemplates.length === 0,
        },
        defaultForeignInvoiceTemplate: {
            label: defaultLabel(
                'offices.invoice-templates-default-foreign',
                'offices.tooltips.invoice-templates-default-foreign'
            ),
            content: templateName(invoiceTemplates.find((template) => template.isDefaultForeign)),
            hidden: invoiceTemplates.length === 0,
        },
        specTemplates: {
            label: t('offices.spec-templates'),
            content: joinTemplateNames(invoiceSpecTemplates),
            chips: chips(invoiceSpecTemplates),
        },
        defaultSpecTemplate: {
            label: defaultLabel(
                'offices.spec-templates-default-local',
                'offices.tooltips.spec-templates-default-local'
            ),
            content: templateName(invoiceSpecTemplates.find((template) => template.isDefault)),
            hidden: invoiceSpecTemplates.length === 0,
        },
        defaultForeignSpecTemplate: {
            label: defaultLabel(
                'offices.spec-templates-default-foreign',
                'offices.tooltips.spec-templates-default-foreign'
            ),
            content: templateName(invoiceSpecTemplates.find((template) => template.isDefaultForeign)),
            hidden: invoiceSpecTemplates.length === 0,
        },
    } satisfies Record<string, Row>;
};

type OfficeRows = ReturnType<typeof officeRows>;

/** The new design's General tab: one card per topic, each editing its own fields in place. */
const OfficeGeneralCards = ({
    office,
    rows,
    allClassifiers,
}: {
    office: Office;
    rows: OfficeRows;
    allClassifiers: ClassifierData[];
}) => {
    const { customFields, customFieldsFiltered } = useCustomFields({
        entityType: EntityType.OFFICE,
        fields: office.customFields,
    });
    // On a draft, the values it was saved without read as missing, and whom it is shared with shows.
    const draftActions = useContext(DraftActionsContext);
    const missingFields = draftActions?.draft?.missingFields;
    // The form has the local address behind this flag; without it the row is not edited and stays as it reads.
    const isLocalizedFieldsEnabled = useIsFeatureEnabled(FeatureFlag.LOCALIZED_FIELDS);
    // A set address has a row of its own and wraps on it as its field does: with the room of the field's pencil
    // after its last word.
    const addressRow = (row: Row): Row => ({
        ...row,
        content: (
            <>
                {row.content}
                <AddressPencilButton aria-hidden disabled className="invisible" />
            </>
        ),
        wide: spansRow(row.content),
    });
    const contactRows: Row[] = withMissing(
        [
            rows.email,
            rows.phone,
            addressRow(rows.address),
            { ...addressRow(rows.localAddress), readOnly: !isLocalizedFieldsEnabled },
        ],
        missingFields
    );
    return (
        <div className={RECORD_GRID_CLASS}>
            <div className="flex min-w-0 flex-col gap-(--card-gap)">
                <OfficeSection id="office" titleId="office" office={office}>
                    {(edit) => (
                        <GridItemList
                            isNew
                            titleId="office"
                            data={withMissing(
                                [
                                    {
                                        ...rows.company,
                                        label: infoLabel(
                                            'offices.main-company',
                                            'offices.tooltips.company',
                                            'justify-between'
                                        ),
                                    },
                                    rows.companies,
                                    rows.name,
                                    {
                                        ...rows.initials,
                                        label: infoLabel('initials', 'offices.tooltips.initials', 'justify-between'),
                                    },
                                    rows.active,
                                ],
                                missingFields
                            )}
                            button={edit}
                        />
                    )}
                </OfficeSection>
                <OfficeSection id="invoicing" titleId="offices.invoicing-and-templates" office={office}>
                    {(edit) => (
                        <GridItemList
                            isNew
                            titleId="offices.invoicing-and-templates"
                            data={[
                                rows.invoiceAbbreviation,
                                rows.invoicingEmail,
                                rows.deadlineEventEmail,
                                rows.invoiceTemplates,
                                rows.defaultInvoiceTemplate,
                                rows.defaultForeignInvoiceTemplate,
                                rows.specTemplates,
                                rows.defaultSpecTemplate,
                                rows.defaultForeignSpecTemplate,
                            ]}
                            button={edit}
                        />
                    )}
                </OfficeSection>
                {customFieldsFiltered.length > 0 ? (
                    <OfficeSection id="custom-fields" titleId="custom-fields.title" office={office}>
                        {(edit) => <CustomFieldCards fields={customFields} classifiers={allClassifiers} edit={edit} />}
                    </OfficeSection>
                ) : null}
                <AuditInfo
                    createdAt={office.createdAt}
                    createdByEmployee={office.createdByEmployee}
                    lastModifiedAt={office.lastModifiedAt}
                    changedByEmployee={office.changedByEmployee}
                />
            </div>
            <div className="flex min-w-0 flex-col gap-(--card-gap)">
                {draftActions ? <DraftSharedWith /> : null}
                <OfficeSection id="contact" titleId="offices.contact-and-address" office={office} rows={contactRows}>
                    {(edit) => (
                        <GridItemList isNew titleId="offices.contact-and-address" data={contactRows} button={edit} />
                    )}
                </OfficeSection>
            </div>
        </div>
    );
};

const OfficePage = (): JSX.Element => {
    const { id: officeId } = useParams();
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/offices');
    const isNew = useIsNewDesign();
    const canEdit = useHasPermissions(UPDATE_OFFICES);

    // Read afresh: a draft completed in its form opens here, where the form's read of the draft may still be cached.
    const { data: office, isLoading: isLoadingOffice } = useGetOfficeByIdQuery(officeId ?? skipToken, {
        refetchOnMountOrArgChange: true,
    });
    const isDraft = !!office?.isDraft;
    const leaveDeleted = useLeaveDeletedEntity('/offices');
    const draftActions = useDraftActions({
        draft: office?.isDraft ? { type: 'office', id: office.id } : null,
        createRoute: '/office/new',
        onDeleted: leaveDeleted,
    });
    const missingFields = draftActions.draft?.missingFields;
    const {
        classifiers,
        allClassifiers,
        isLoading: isLoadingClassifiers,
    } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });

    const { fixedSender, isKnown } = useFixedDeadlineEventSender();
    const rows = useMemo(
        () => (office ? officeRows(office, classifiers, { fixedSender, isKnown }) : undefined),
        [office, classifiers, fixedSender, isKnown]
    );
    const generalRows: Row[] = useMemo(
        () =>
            rows
                ? withMissing(
                      [
                          rows.company,
                          rows.companies,
                          rows.name,
                          rows.initials,
                          rows.email,
                          rows.phone,
                          rows.address,
                          rows.invoiceAbbreviation,
                          rows.localAddress,
                          rows.active,
                          rows.invoicingEmail,
                          rows.deadlineEventEmail,
                          rows.invoiceTemplates,
                          rows.specTemplates,
                      ],
                      missingFields
                  )
                : [],
        [rows, missingFields]
    );

    const tabs = useMemo<TabType[]>(() => {
        if (!office || !rows) return [];
        const officeCustomFields = (office.customFields as CustomField[]) ?? [];
        return [
            {
                id: 'general',
                label: t('matters.general'),
                component: isNew ? (
                    <OfficeGeneralCards office={office} rows={rows} allClassifiers={allClassifiers} />
                ) : (
                    <div className="flex flex-col gap-6">
                        <GridItemList titleId="matters.overview" data={generalRows} key="office-general" />
                        {isDraft ? <DraftSharedWith /> : null}
                        {officeCustomFields.length > 0 ? (
                            <CustomFieldView fields={officeCustomFields} classifiers={allClassifiers} />
                        ) : null}
                        <AuditInfo
                            createdAt={office.createdAt}
                            createdByEmployee={office.createdByEmployee}
                            lastModifiedAt={office.lastModifiedAt}
                            changedByEmployee={office.changedByEmployee}
                        />
                    </div>
                ),
            },
        ];
    }, [office, rows, generalRows, allClassifiers, isNew, isDraft]);

    if (isLoadingOffice || isLoadingClassifiers) {
        return <Loader />;
    }

    if (!office) {
        return <></>;
    }

    const name = office.name || (isDraft ? t('new-entity.office') : '-');
    const title = `${name}${officeInactiveSuffix(office) ?? ''}`;
    const draft = draftActions.draft;
    const draftNotice = draft ? <DraftNotice actions={draftActions} /> : null;
    const tabsGallery = (
        <TabsGallery
            tabs={tabs}
            tabGroupId="office-page"
            trackTabsInHistoryState
            usePathBasedRouting
            wrapperClassName={RECORD_TABS_CLASS}
            panelClassName={isNew ? RECORD_PANEL_CLASS : 'min-h-0 flex-1 overflow-y-auto'}
            className={isNew ? RECORD_TAB_STRIP_CLASS : undefined}
        />
    );

    if (isNew) {
        const header = (
            <RecordHeader
                number={office.abbreviation}
                chips={
                    <>
                        {isDraft ? <DraftChip /> : null}
                        <ActiveChip isActive={office.isActive !== false} />
                    </>
                }
                title={name}
                pin={isDraft ? undefined : { kind: 'office', id: office.id }}
                meta={
                    <>
                        {office.company?.name ? (
                            <MetaItem label={<FormattedMessage id="offices.main-company" />}>
                                {office.company.name}
                            </MetaItem>
                        ) : null}
                        {office.emailAddress ? (
                            <MetaItem label={<FormattedMessage id="email" />}>
                                <a className={META_LINK_CLASS} href={`mailto:${office.emailAddress}`}>
                                    {office.emailAddress}
                                </a>
                            </MetaItem>
                        ) : null}
                        {office.phoneNumber ? (
                            <MetaItem label={<FormattedMessage id="phone-number" />}>{office.phoneNumber}</MetaItem>
                        ) : null}
                        {draft ? <DraftMeta draft={draft} /> : null}
                    </>
                }
                actions={isDraft ? <DraftRecordActions actions={draftActions} /> : undefined}
            />
        );
        return (
            <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
                <RecordPage
                    header={
                        draftNotice ? (
                            <div className="flex flex-col gap-4">
                                {header}
                                {draftNotice}
                            </div>
                        ) : (
                            header
                        )
                    }
                >
                    <SectionEditProvider canEdit={canEdit && !isDraft}>{tabsGallery}</SectionEditProvider>
                </RecordPage>
                {draftActions.shareDialog}
            </DraftActionsContext.Provider>
        );
    }

    return (
        <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
            <PaddedLayout className="flex h-full flex-col overflow-hidden">
                <PageHeader
                    title={title}
                    showBreadcrumb
                    onBack={goBack}
                    buttons={
                        <div className="flex items-center justify-end gap-2">
                            {isDraft ? (
                                <DraftRecordActions actions={draftActions} />
                            ) : (
                                <PermissionButton
                                    permissions={UPDATE_OFFICES}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => navigateWithReturn(`/office/${officeId}/edit`)}
                                >
                                    <FormattedMessage id="edit" />
                                </PermissionButton>
                            )}
                        </div>
                    }
                >
                    {draft ? (
                        <div className="flex flex-wrap items-center gap-2 py-3">
                            <DraftChip />
                            <DraftMeta draft={draft} />
                        </div>
                    ) : null}
                </PageHeader>
                {draftNotice ? <div className="mb-4">{draftNotice}</div> : null}
                {tabsGallery}
            </PaddedLayout>
            {draftActions.shareDialog}
        </DraftActionsContext.Provider>
    );
};

export default OfficePage;
